import React, { useEffect, useState } from 'react';
import { Form } from 'src/core/forms';
import { DispatchProp, useDispatch } from 'react-redux';
import { ErrorText } from 'src/components/Forms/ErrorFor';
import { CustomizationSkeleton } from 'src/elements/Skeletons/Skeletons';
import { HAVE_MY_LOGO } from 'src/constants/artwork';

import { paths } from '../../../config';
import { Actions } from '../../Forms/Actions';
import { ButtonLink } from '../../Button/ButtonLink';
import { InfoBlock } from '../InfoBlock';
import { ApplicationType, Step } from '../feature/customisation.types';
import { useCustomisation } from '../hooks/useCustomisation';
import { ConfigSubmit, CustomisationMsg } from '../feature/customisation.actions';
import { CustomisationState } from '../feature/customisation.reducer';
import { ConfigureFields } from '../steps/ConfigureFields';

import classes from './Pages.scss';

type Props = {
    applicationType: ApplicationType;
    initialValues: FormInitialValues;
    locationNames: string[];
} & DispatchProp;

export const Configure: React.FC<Props> = (props) => {
    function submitConfiguration(values) {
        const output: ConfigSubmit = {
            textLineOne: values.textLineOne || null,
            textLineTwo: values.textLineTwo || null,
            textLineThree: values.textLineThree || null,
            color: values.color || null,
            font: values.font || null,
            image: values.image || null,
            isContactRequested: ['contact', HAVE_MY_LOGO].includes(values.isContactRequested),
            customisationName: values.customisationName || null,
            specialInstructions: values.specialInstructions || null,
            is_artwork_missing: values.isContactRequested === HAVE_MY_LOGO,
        };
        props.dispatch(CustomisationMsg('Customisation.ConfigSubmit', output));
    }
    const dispatch = useDispatch();
    const [error, setError] = useState<string | undefined>(undefined);

    useEffect(() => {
        const locationNames = Object.keys(props.locationNames).filter((key) => props.locationNames[key] === true);
        const hasSelections = locationNames.length > 0;
        if (error && hasSelections) {
            setError(undefined);
        }
        dispatch(CustomisationMsg('Customisation.EstimatePrices', { locations: props.locationNames }));
    }, [dispatch, error, props.locationNames]);

    return (
        <div className={classes.fadein}>
            <h2 className={classes.header}>
                {props.applicationType === 'Text' ? '5. Configure your text logo' : '5. Configure your logo'}
            </h2>
            {error && <ErrorText text={error} />}
            <Form onSubmit={submitConfiguration} className={classes.form} initialValues={props.initialValues}>
                <ConfigureFields
                    applicationType={props.applicationType}
                    initialValues={props.initialValues}
                    locations={props.locationNames}
                />
                <Actions primaryText="Finish" primaryVariant="secondary" direction="reverse" variant="space-between">
                    <ButtonLink variant="light" to={paths.customisation.type}>
                        Back a step
                    </ButtonLink>
                </Actions>
            </Form>
            <InfoBlock />
        </div>
    );
};

export const PageConfigureConnected: React.FC = () => {
    const { loadStep, isPending, state } = useCustomisation();
    const dispatch = useDispatch();

    useEffect(() => {
        setTimeout(() => {
            loadStep({ step: Step.Config });
        });
    }, [loadStep]);

    if (isPending) return <CustomizationSkeleton />;

    return (
        <Configure
            applicationType={state.application_type}
            locationNames={state.locationNames}
            initialValues={stateToFormValues(state)}
            dispatch={dispatch}
        />
    );
};

export type FormInitialValues = {
    isContactRequested: 'upload' | 'contact';
    textLineOne: string | null;
    textLineTwo: string | null;
    textLineThree: string | null;
    color: string | null;
    font: string | null;
    specialInstructions: string | null;
    customisationName: string | null;
    image: string | null;
};

function stateToFormValues(state: CustomisationState): FormInitialValues {
    return {
        isContactRequested: state.isContactRequested ? 'contact' : 'upload',
        textLineOne: state.textLineOne,
        textLineTwo: state.textLineTwo,
        textLineThree: state.textLineThree,
        color: state.color || 'White',
        font: state.font || 'Arial',
        specialInstructions: state.specialInstructions,
        customisationName: state.customisationName,
        image: state.image,
    };
}
